import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LabeledInputMixin } from '../../mixins/components/LabeledInputMixin';
import { identifier } from '../../util/identifier';
import { FormFieldContext } from '../../js/FormFieldContext';
export var StyledLabeledInput = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledLabeledInput",
  componentId: "c11n-8-64-0__sc-1ai93v3-0"
})(["", ""], LabeledInputMixin);
/**
 * Compose input text field components ([`Input`](#/Form%20components?id=input)) with a
 * [`Label`](#/Form%20components?id=label).
 *
 * @since 8.23.0
 */

var LabeledInput = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var disabled = props.disabled,
      error = props.error,
      fluid = props.fluid,
      input = props.input,
      label = props.label,
      readOnly = props.readOnly,
      required = props.required;
  var inputClone = /*#__PURE__*/React.cloneElement(input, {
    fluid: fluid,
    size: 'lg',
    placeholder: input.props && input.props.placeholder ? input.props.placeholder : ' '
  });
  var context = Object.assign({}, React.useContext(FormFieldContext), {
    // Create a default id to be used with the input and labels
    controlId: React.useRef(identifier()).current
  });

  if (typeof disabled !== 'undefined') {
    context.disabled = disabled;
  }

  if (typeof error !== 'undefined') {
    context.error = error;
  }

  if (typeof readOnly !== 'undefined') {
    context.readOnly = readOnly;
  }

  if (typeof required !== 'undefined') {
    context.required = required;
  } // Note: selection control components should come first in the DOM tree so that
  // labels can trigger off :hover/:focus states.


  return /*#__PURE__*/React.createElement(StyledLabeledInput, {
    ref: ref
  }, /*#__PURE__*/React.createElement(FormFieldContext.Provider, {
    value: context
  }, inputClone, label));
});
LabeledInput.displayName = 'LabeledInput';
LabeledInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Set the input as disabled.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  disabled: PropTypes.bool,

  /**
   * Signals that there is an error with the value and sets the
   * [aria-invalid](https://www.w3.org/TR/wai-aria/#aria-invalid) property.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  error: PropTypes.bool,

  /**
   * If `true`, this will span the entire width.
   */
  fluid: PropTypes.bool,

  /**
   * A [`Input`](#/Form%20components?id=input) component.
   */
  input: PropTypes.node.isRequired,

  /**
   * A [`Label`](#/Form%20components?id=label) component.
   */
  label: PropTypes.node.isRequired,

  /**
   * Mark the input as read-only with the native [`readonly`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly)
   * attribute.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   *
   * @since 8.27.0
   */
  readOnly: PropTypes.bool,

  /**
   * Mark the input as required with the native [`required`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/required)
   * attribute.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  required: PropTypes.bool
} : {};
LabeledInput.defaultProps = {
  fluid: true
};
export default LabeledInput;